import { useIntervalFn } from "@vueuse/core";
import { BuildMode } from "@/types";

export enum Event {
  ShareActionFallbacked = "Share action fallback",
}

export type SentryProcessorEvent = {
  message?: string;
  release?: string;
  environment?: string;
  request?: {
    url?: string;
  };
  exception?: {
    values?: Array<{
      value?: string;
      stacktrace?: {
        frames?: Array<{ filename?: string }>;
      };
    }>;
  };
};

export type SentryProcessorEventHint = {
  originalException?: unknown;
};

// --- Browser / WebView / extension junk (never actionable) ---
// --- Stale-deploy / chunk-load noise ---
// --- Network / connectivity ---
const IGNORED_ERROR_PATTERNS: Array<string | RegExp> = [
  "Java object is gone", // Android in-app WebView
  "window.webkit.messageHandlers", // iOS WebView injection
  "Non-Error promise rejection captured with value", // browser extension / 3rd-party script
  "Importing a module script failed",
  "Unable to preload CSS",
  /Loading chunk \S+ failed/i,
  "ChunkLoadError",
  "Network Error",
  "Request failed with status 0",
];

// Drop anything originating in a browser extension.
const DENIED_URL_PATTERNS: RegExp[] = [/extensions\//i, /^chrome:\/\//i, /^moz-extension:\/\//i, /^safari-web-extension:\/\//i];

const SENTRY_READY_POLL_INTERVAL_MS = 500;
const SENTRY_READY_MAX_POLL_ATTEMPTS = 20;

export const initEventFilter = (): void => {
  if (!isSentryAllowed()) {
    return;
  }

  let attempts = 0;
  const { pause } = useIntervalFn(
    () => {
      attempts += 1;

      if (typeof window.Sentry !== "undefined") {
        window.Sentry.addEventProcessor(filterEvent);
        pause();
        return;
      }

      if (attempts >= SENTRY_READY_MAX_POLL_ATTEMPTS) {
        pause();
        requireSentryAvailability();
      }
    },
    SENTRY_READY_POLL_INTERVAL_MS,
    { immediate: true }
  );
};

const requireSentryAvailability = (): void => {
  const mode = import.meta.env.MODE as BuildMode;
  if (mode !== BuildMode.Production) {
    return;
  }

  if (typeof window.Sentry === "undefined") {
    throw new Error("DEV: Sentry not found!");
  }
};

const filterEvent = (event: SentryProcessorEvent, hint: SentryProcessorEventHint): SentryProcessorEvent | null => {
  const message = getEventMessage(event, hint);

  if (matchesAnyPattern(message, IGNORED_ERROR_PATTERNS)) {
    return null;
  }

  if (isFromDeniedUrl(event)) {
    return null;
  }

  event.release = __APP_VERSION__;
  event.environment = import.meta.env.MODE;

  return event;
};

const getEventMessage = (event: SentryProcessorEvent, hint: SentryProcessorEventHint): string => {
  if (hint.originalException instanceof Error) {
    return hint.originalException.message;
  }
  return event.message ?? event.exception?.values?.[0]?.value ?? "";
};

const matchesAnyPattern = (value: string, patterns: Array<string | RegExp>): boolean => {
  return patterns.some((pattern) => (typeof pattern === "string" ? value.includes(pattern) : pattern.test(value)));
};

const isFromDeniedUrl = (event: SentryProcessorEvent): boolean => {
  const frames = event.exception?.values?.flatMap((value) => value.stacktrace?.frames ?? []) ?? [];
  const urls = [event.request?.url, ...frames.map((frame) => frame.filename)].filter((url): url is string => Boolean(url));

  return urls.some((url) => DENIED_URL_PATTERNS.some((pattern) => pattern.test(url)));
};

export const trackEvent = async (event: Event, severity: string = "info"): Promise<void> => {
  if (!isSentryAllowed()) {
    return;
  }

  if (typeof window.Sentry === "undefined") {
    console.warn("Sentry not found.");
  } else {
    window.Sentry.captureMessage(event, severity);
  }
};

export const captureException = (error: unknown): void => {
  if (!isSentryAllowed()) {
    return;
  }

  if (typeof window.Sentry === "undefined") {
    console.warn("Sentry not found.");
    return;
  }

  window.Sentry.captureException(error);
};

const isSentryAllowed = (): boolean => {
  const allowedModes = [BuildMode.Production, BuildMode.Beta];
  const mode = import.meta.env.MODE as BuildMode;
  return allowedModes.includes(mode);
};
